const WEBM_AUDIO_MIME_TYPES = ["audio/webm;codecs=opus", "audio/webm"] as const;
const MP4_AUDIO_MIME_TYPES = [
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
] as const;
const OGG_AUDIO_MIME_TYPES = ["audio/ogg;codecs=opus", "audio/ogg"] as const;

const isWebKitBrowser = (): boolean => {
  if (typeof navigator === "undefined") {
    return false;
  }

  const { userAgent } = navigator;
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return true;
  }

  return (
    /AppleWebKit/i.test(userAgent) &&
    !/Chrome|Chromium|CriOS|Edg\//i.test(userAgent)
  );
};

const getAudioMimeTypeCandidates = (): string[] => {
  const webm = [...WEBM_AUDIO_MIME_TYPES];
  const mp4 = [...MP4_AUDIO_MIME_TYPES];
  const ogg = [...OGG_AUDIO_MIME_TYPES];

  return isWebKitBrowser()
    ? [...mp4, ...webm, ...ogg]
    : [...webm, ...mp4, ...ogg];
};

const isRecordingSupported = (): boolean =>
  typeof MediaRecorder !== "undefined";

const probeAudioMimeType = (
  stream: MediaStream,
  mimeType?: string
): string | null => {
  try {
    const recorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);
    recorder.start();
    recorder.stop();
    return mimeType ?? recorder.mimeType;
  } catch {
    return null;
  }
};

const resolveSupportedAudioMimeType = (stream: MediaStream): string | null => {
  if (!isRecordingSupported()) {
    return null;
  }

  const resolvedCandidate = getAudioMimeTypeCandidates().reduce<string | null>(
    (resolved, mimeType) => resolved ?? probeAudioMimeType(stream, mimeType),
    null
  );

  return resolvedCandidate ?? probeAudioMimeType(stream);
};

const formatRecordingDuration = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
};

export {
  formatRecordingDuration,
  getAudioMimeTypeCandidates,
  isRecordingSupported,
  resolveSupportedAudioMimeType,
};
